import { useState } from "react"

import { useForm } from "@tanstack/react-form"

import { ChevronLeft } from "lucide-react"

import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Separator } from "@/components/ui/Separator"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/Hovercard"
import { FormInput } from "@/components/FormInput"
import { FormInputError } from "@/components/FormInputError"

import { useMountEffect } from "@/hooks/useMountEffect"

import { LoginPageController } from "../LoginPage.controller"
import { sanitizeClasscode } from "./classcode-utils"

export function ClasscodeLogin({
  controller,
}: {
  controller: LoginPageController
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [isClassCodeValidated, setIsClassCodeValidated] = useState(false)
  const form = useForm({
    defaultValues: {
      classCode: controller.classCode || "",
      name: controller.name || "",
    },
    onSubmit: () => {
      controller.login()
    },
  })
  useMountEffect(() => {
    if (controller.classCode) {
      form.validateField("classCode", "change")
    }
  })
  function handleBackButton() {
    controller.goBack()
    setIsClassCodeValidated(false)
    form.reset()
  }

  return (
    <div className="tw-bg-white tw-shadow-lg tw-rounded-lg tw-w-[350px] tw-max-w-full tw-flex tw-flex-col">
      <div className="tw-h-[10em] tw-relative tw-flex tw-items-center tw-justify-center">
        {isClassCodeValidated && (
          <Button
            variant="ghost"
            size="icon"
            className="tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-emerald-600 hover:tw-bg-emerald-50 tw-h-20 tw-w-20 [&_svg]:tw-size-10"
            onClick={handleBackButton}
            aria-label="Go back"
          >
            <ChevronLeft strokeWidth={2} />
          </Button>
        )}
        <h1 className="tw-text-4xl tw-text-gray-700">
          {isClassCodeValidated ? controller.title : "Start Working"}
        </h1>
      </div>

      <div className="tw-px-12 tw-pb-1 tw-flex tw-flex-col tw-gap-4">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="tw-flex tw-flex-col tw-gap-4"
        >
          <form.Field
            name="classCode"
            validators={{
              onChangeAsyncDebounceMs: 500,
              onChangeAsync: async ({ value }: { value: string }) => {
                setIsLoading(true)
                const result = await controller.checkClassCodeValidity(value)
                setIsLoading(false)

                if (result.isValid) {
                  setIsClassCodeValidated(true)
                } else {
                  setIsClassCodeValidated(false)
                  controller.goBack()
                }

                return result.errorMessage
              },
            }}
          >
            {(field) => {
              function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
                e.preventDefault()
                const pastedText = e.clipboardData.getData("text")
                const cleanText = sanitizeClasscode(pastedText)
                field.handleChange(cleanText)
              }

              function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
                const sanitizedValue = sanitizeClasscode(e.target.value)
                field.handleChange(sanitizedValue)
              }

              return (
                <>
                  <FormInput
                    field={field}
                    label="Class Code"
                    onPaste={handlePaste}
                    onChange={handleChange}
                    autoCapitalize="characters"
                    autoFocus
                    disabled={controller.isLoading}
                  />
                  <FormInputError errors={field.state.meta.errors} />
                </>
              )
            }}
          </form.Field>

          {isClassCodeValidated && !controller.hideStudentWork && (
            <form.Field
              name="name"
              validators={{
                onChangeAsyncDebounceMs: 500,
                onChangeAsync: async ({ value }: { value: string }) => {
                  return controller.checkNameValidity(value)
                },
              }}
            >
              {(field) => (
                <>
                  <FormInput
                    field={field}
                    label="Name"
                    autoComplete="off"
                    autoFocus
                  />
                  <FormInputError errors={field.state.meta.errors} />
                </>
              )}
            </form.Field>
          )}

          {isClassCodeValidated && (
            <div className="tw-flex tw-flex-col tw-gap-4 tw-py-2">
              <form.Subscribe
                selector={(state) => [
                  state.values.name,
                  state.fieldMeta.name?.errors,
                ]}
              >
                {([nameValue, nameErrors]) => (
                  <Button
                    type="submit"
                    className="tw-w-full tw-h-14 tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-text-white tw-text-2xl"
                    disabled={!nameValue || !!nameErrors?.length}
                  >
                    {controller.anonStudentLoginButtonMessage}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          )}
        </form>

        {!isClassCodeValidated && (
          <div className="tw-flex tw-justify-center tw-items-center">
            <HoverCard>
              <HoverCardTrigger className="tw-text-xl tw-text-emerald-600 tw-underline tw-underline-offset-4 tw-cursor-help hover:tw-text-emerald-700">
                What's a Class Code?
              </HoverCardTrigger>
              <HoverCardContent className="tw-w-100 tw-text-lg tw-bg-white tw-p-4 tw-shadow-xl tw-rounded-lg tw-border tw-border-gray-200">
                <p className="tw-text-gray-700 tw-text-center">
                  <span className="tw-font-bold">
                    Class codes come from an assignment within the Classkick
                    app.
                  </span>
                  <br />
                  Ask your teacher for a classcode!
                </p>
              </HoverCardContent>
            </HoverCard>
          </div>
        )}

        <div className="tw-flex tw-items-center tw-gap-2 tw-my-2">
          <Separator className="tw-flex-1 tw-h-0.5 tw-bg-gray-600" />
          <span className="tw-text-gray-600 tw-text-xl tw-font-medium tw-leading-none">
            {isClassCodeValidated && controller.showPro
              ? "Continue with Portfolio"
              : "Or"}
          </span>
          <Separator className="tw-flex-1 tw-h-0.5 tw-bg-gray-600" />
        </div>

        <div className="tw-flex tw-items-center tw-gap-4 tw-pb-8">
          <Button
            type="button"
            variant="outline"
            className="tw-w-full tw-h-12 tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-text-white tw-text-2xl"
          >
            Log In
          </Button>
          <Button
            type="button"
            variant="outline"
            className="tw-w-full tw-h-12 tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-text-white tw-text-2xl"
          >
            Sign Up
          </Button>
        </div>
      </div>
    </div>
  )
}
